import {Component, Input, OnInit} from '@angular/core';
import {
    BaseStepperFormComponent,
    filterList,
    LoadingService,
    Menu,
    MenuItemModifier,
    MenuItemModifierGroup,
    MenuItemModifierSelection,
    MenuItemModifierSelectionOption,
    MenuLabel,
    MenuModifier,
    MenuModifierGroup,
    MenuModifierSelection,
    MenuService,
    MenuTopping,
    MenuToppingModifier,
    MenuToppingModifierGroup,
    MenuToppingModifierSelection,
    MenuToppingModifierSelectionOption,
    MenuToppingService,
    sortAlphabetical,
    sortBySortOrder,
    ToastService
} from 'brewbill-lib';
import {ModalController} from '@ionic/angular';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';

@Component({
    selector: 'bb-edit-menu-topping',
    templateUrl: './edit-menu-topping.component.html',
    styleUrls: ['./edit-menu-topping.component.scss'],
})
export class EditMenuToppingComponent extends BaseStepperFormComponent implements OnInit {
    @Input() topping: MenuTopping;
    @Input() addMenuLabel: any;
    @Input() addMenuModifier: any;
    @Input() addMenuModifierSelection: any;
    @Input() addMenuModifierGroup: any;

    protected readonly sortAlphabetical = sortAlphabetical;

    selectedModifiers = [];
    selectedLabels: MenuLabel[] = [];

    menu: Menu;
    informationFormGroup: FormGroup = new FormGroup({});

    constructor(
        modalController: ModalController,
        private formBuilder: FormBuilder,
        private toastService: ToastService,
        private menuService: MenuService,
        private menuToppingService: MenuToppingService,
        private loadingService: LoadingService
    ) {
        super(modalController);
    }

    ngOnInit() {
        this.menu = this.menuService.currentValue;
        this.topping = this.topping == null ? new MenuTopping() : this.topping;

        let tempModifiers = [];
        if (!!this.topping.modifiers) {
            tempModifiers = [...this.topping.modifiers];
        }
        if (!!this.topping.modifierSelections) {
            tempModifiers = tempModifiers.concat(this.topping.modifierSelections);
        }
        if (!!this.topping.modifierGroups) {
            tempModifiers = tempModifiers.concat(this.topping.modifierGroups);
        }

        this.selectedModifiers = sortBySortOrder(tempModifiers);
        this.selectedLabels = this.topping.labels == null ? [] : [...this.topping.labels];

        this.informationFormGroup = this.formBuilder.group(
            {
                id: this.topping.id,
                parentId: this.menu.id,
                available: this.topping.available == null ? true : this.topping.available,
                name: [this.topping.name, Validators.required],
                description: this.topping.description
            }
        );

        this.subscribe(this.menuService.current.subscribe(m => {
            this.menu = !!m ? new Menu(m) : null;
        }));
    }

    valid() {
        return this.informationFormGroup.valid;
    }

    removeLabel(item: MenuLabel) {
        this.selectedLabels = this.selectedLabels.filter(t => t.id !== item.id);
    }

    markLabels(items: any[], selectedItems: any[], selectionParam: string, disabledParam: string) {
        items.forEach(i => {
            i[selectionParam] = selectedItems.some(s => s.id === i.id);
        });
    }

    updateSelectedLabels(labels: any[]) {
        const update = this.selectedLabels.filter(t =>
            labels.some(i => i.id === t.id));

        labels.filter(i => !update.some(t => t.id === i.id)).forEach(s => {
            update.push(s);
        });

        this.selectedLabels = update;
    }

    async openAddMenuLabel() {
        const l = await this.addMenuLabel(null);
        if (l != null) {
            this.selectedLabels = [...this.selectedLabels, l];
        }
    }

    buildModifierTopping(item: any, sortOrder: number) {
        if (!!item.options) {
            const options: MenuToppingModifierSelectionOption[] = [];
            item.options.forEach(o => options.push(new MenuItemModifierSelectionOption({
                menuModifier: o.menuModifier,
                additionalPrice: null
            })));

            return new MenuToppingModifierSelection({
                menuModifierSelection: item as MenuModifierSelection,
                sortOrder,
                options
            });
        } else if (!!item.selections) {
            return new MenuToppingModifierGroup({
                menuModifierGroup: item as MenuModifierGroup,
                sortOrder
            });
        } else {
            return new MenuToppingModifier({
                menuModifier: item as MenuModifier,
                additionalPrice: null,
                sortOrder
            });
        }
    }

    async openAddMenuModifier() {
        const t = await this.addMenuModifier(null);
        if (t != null) {
            const mit = new MenuItemModifier();
            mit.menuModifier = t;
            mit.sortOrder = this.selectedModifiers.length;
            this.selectedModifiers = [...this.selectedModifiers, mit];
        }
    }

    async openAddMenuModifierSelection() {
        const t = await this.addMenuModifierSelection(null);
        if (t != null) {
            const options: MenuItemModifierSelectionOption[] = [];
            t.options.forEach(o => options.push(new MenuItemModifierSelectionOption({
                menuModifier: o.menuModifier,
                additionalPrice: null
            })));

            const mit = new MenuItemModifierSelection({
                menuModifierSelection: t as MenuModifierSelection,
                sortOrder: this.selectedModifiers.length,
                options
            });
            this.selectedModifiers = [...this.selectedModifiers, mit];
        }
    }

    async openAddMenuModifierGroup() {
        const t = await this.addMenuModifierGroup(null);
        if (t != null) {
            const mit = new MenuItemModifierGroup({
                menuModifierGroup: t as MenuModifierGroup,
                sortOrder: this.selectedModifiers.length
            });
            this.selectedModifiers = [...this.selectedModifiers, mit];
        }
    }

    filterByName(items: any[], filter: string) {
        return filterList(items, filter, 'name');
    }

    onSubmit() {
        this.loadingService.present();
        const menuTopping: MenuTopping = new MenuTopping(this.informationFormGroup.value);
        menuTopping.labels = this.selectedLabels;

        menuTopping.modifiers = this.selectedModifiers.filter(m => !!m.menuModifier);
        menuTopping.modifierSelections = this.selectedModifiers.filter(m => !!m.menuModifierSelection);
        menuTopping.modifierGroups = this.selectedModifiers.filter(m => !!m.menuModifierGroup);

        if (menuTopping.id != null) {
            this.menuToppingService.update(menuTopping).subscribe(
                async (t: MenuTopping) => {
                    this.loadingService.dismiss();
                    await this.close(t);
                }, error => {
                    if (error.status === 422) {
                        this.toastService.error('The topping is out of sync from a previous change. Please refresh to make changes.');
                    }
                    this.close();
                }
            );
        } else {
            this.menuToppingService.create(menuTopping).subscribe(
                async (t: MenuTopping) => {
                    this.loadingService.dismiss();
                    await this.close(t);
                }
            );
        }
    }
}
